import Marquee from "react-fast-marquee";
import { Heart } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, stagger, item } from "@/components/Reveal";
import { CtaBand } from "@/components/Cta";
import { VALUES, IMAGES } from "@/data/site";
import { useSEO, webPageSchema } from "@/lib/seo";
import { PAGE_META } from "@/data/page_meta";


export default function About() {
  useSEO({ ...PAGE_META.ourStory, jsonLd: webPageSchema({ path: PAGE_META.ourStory.path, name: PAGE_META.ourStory.title, description: PAGE_META.ourStory.description, type: "AboutPage" }) });
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="pt-36 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(242,169,184,0.12),transparent_45%)]" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-leaf mb-4" data-testid="our-story-eyebrow">Our Story</p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter text-navy leading-[0.98] max-w-4xl">
              It started with one simple belief: Cleaning shouldn't come at the cost of your health.
            </h1>
            <p className="mt-7 text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
              What began as a personal commitment to protecting my family has grown into a mission to create
              cleaner, safer spaces where people live, work, and gather.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FOUNDER STORY */}
      <section className="py-16 md:py-24" data-testid="founder-story">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative rounded-[2rem] rounded-tr-md overflow-hidden aspect-[4/5] shadow-2xl">
              <img src={IMAGES.founder} alt="CJB Clean founder holding her newborn" className="w-full h-full object-cover object-top" data-testid="founder-photo" />
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.25em] text-leaf mb-5">Why I Started CJB Clean</p>
            </Reveal>
            <div className="space-y-6 text-lg md:text-xl text-navy/80 leading-relaxed font-light">
              <Reveal delay={0.05}>
                <p>
                  CJB Clean was born during pregnancy and motherhood — a season of reading every label
                  and asking harder questions about what we bring into our homes.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p>
                  While researching the ingredients in everyday cleaning products, I realized how many traditional
                  cleaners rely on <span className="text-navy font-medium">harsh chemicals that families often overlook</span> —
                  the very products meant to make a home "clean."
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <p>
                  That realization inspired something better: a cleaning service built on Green Cleaning as the standard.
                  The goal is simple — provide healthier spaces without asking families or businesses to compromise on
                  the products used in their environments.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="py-20 md:py-28 bg-navy text-white overflow-hidden" data-testid="core-values">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.25em] text-leaf mb-4">Core Values</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight leading-none">What we stand for.</h2>
          </Reveal>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {VALUES.map((v) => (
              <motion.div key={v.n} variants={item} className="border-t border-white/15 pt-5" data-testid={`value-${v.n}`}>
                <p className="font-heading text-4xl font-bold text-leaf/80 mb-3">{v.n}</p>
                <h3 className="font-heading text-lg font-semibold mb-2">{v.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="py-5 bg-leaf" data-testid="about-marquee">
        <Marquee speed={35} gradient={false} autoFill>
          {["Healthy Spaces", "Smarter Solutions", "Reliable Service"].map((t) => (
            <span key={t} className="mx-10 font-heading text-2xl font-medium text-white/90 flex items-center gap-10">{t} <Heart className="w-4 h-4" /></span>
          ))}
        </Marquee>
      </div>

      <CtaBand />
    </div>
  );
}
